/**
 * @module State hooks for the Tiptap prompt editor.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE), split out of the component so
 * each hook owns one concern: the prop mirrors plus controlled controller,
 * and the store sync.
 */

import type { Editor } from "@tiptap/react";
import type { ServerProviderSkill } from "@cmmd-forge/contracts";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import type { TerminalContextDraft } from "~/lib/terminalContext";
import { skillSignature, terminalContextSignature } from "./composerContextSignatures";
import { buildDocJson, type SkillMeta } from "~/composer-rich-text-doc";
import { resolveSkillMeta } from "./composerPromptEditorChips";
import {
  type ComposerPromptEditorController,
  createComposerPromptEditorController,
} from "./composerPromptEditorController";

interface ControlledSyncInput {
  readonly editor: Editor | null;
  readonly controller: ComposerPromptEditorController;
  readonly value: string;
  readonly cursor: number;
  readonly richText: boolean;
  readonly terminalContexts: ReadonlyArray<TerminalContextDraft>;
  readonly skills: ReadonlyArray<ServerProviderSkill>;
}

/**
 * Controlled value and cursor from the store: history recall, chip insertion,
 * draft hand-offs, and chip metadata refreshes. Chip metadata changes rewrite
 * the document without a prompt change so the node views re-read their labels.
 */
export function useComposerControlledSync(input: ControlledSyncInput): void {
  const { editor, controller, value, cursor, richText, terminalContexts, skills } = input;
  const appliedInitialSelection = useRef(false);
  const contextsSignatureRef = useRef("");
  const skillsSignatureRef = useRef("");
  useLayoutEffect(() => {
    if (!editor) return;
    const initialSelection = !appliedInitialSelection.current;
    appliedInitialSelection.current = true;
    const contextsSignature = terminalContextSignature(terminalContexts);
    const skillsSignatureValue = skillSignature(skills);
    const contextsChanged = contextsSignatureRef.current !== contextsSignature;
    const skillsChanged = skillsSignatureRef.current !== skillsSignatureValue;
    contextsSignatureRef.current = contextsSignature;
    skillsSignatureRef.current = skillsSignatureValue;
    // Build from this render's skills: the ref-based callback still holds the
    // previous array until the passive effect runs.
    const labelFor = (name: string): SkillMeta => resolveSkillMeta(skills, name);
    const metadataChanged = contextsChanged || skillsChanged;
    if (!initialSelection && controller.snapshotRef.current.value === value) {
      if (metadataChanged) {
        controller.rebuild(
          editor,
          buildDocJson(value, labelFor, { styling: richText, terminalContexts }),
        );
        // The rebuild moves the caret; always restore the requested one.
        controller.applyControlled(editor, value, cursor);
        return;
      }
      // The editor already reports this prompt; only the caret may move.
      if (controller.snapshotRef.current.cursor !== cursor) {
        controller.applyControlled(editor, value, cursor);
      }
      return;
    }
    if (controller.snapshotRef.current.value !== value || metadataChanged) {
      controller.rebuild(
        editor,
        buildDocJson(value, labelFor, { styling: richText, terminalContexts }),
      );
    }
    controller.applyControlled(editor, value, cursor);
  }, [controller, cursor, editor, richText, terminalContexts, skills, value]);
}

/**
 * Prop mirrors plus the controlled-state controller, created once per mount:
 * the editor config and controller were created once, so the live callbacks
 * reach them through refs, and the controller owns the snapshot refs.
 */
export function useComposerEditorState(input: {
  readonly initialValue: string;
  readonly initialCursor: number;
  readonly initialTerminalContextIds: ReadonlyArray<string>;
  readonly onChange: (
    nextValue: string,
    nextCursor: number,
    expandedCursor: number,
    cursorAdjacentToMention: boolean,
    terminalContextIds: string[],
  ) => void;
  readonly onCommandKeyDown:
    | ((
        key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
        event: KeyboardEvent,
        editorValue: string,
      ) => boolean)
    | undefined;
  readonly onRemoveTerminalContext: (contextId: string) => void;
  readonly skills: ReadonlyArray<ServerProviderSkill>;
  readonly value: string;
}): {
  readonly controller: ComposerPromptEditorController;
  readonly isEmpty: boolean;
  readonly onChangeRef: React.RefObject<
    ComposerPromptEditorController extends never
      ? never
      : (
          nextValue: string,
          nextCursor: number,
          expandedCursor: number,
          cursorAdjacentToMention: boolean,
          terminalContextIds: string[],
        ) => void
  >;
  readonly onCommandKeyDownRef: React.RefObject<
    | ((
        key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
        event: KeyboardEvent,
        editorValue: string,
      ) => boolean)
    | undefined
  >;
  readonly onRemoveTerminalContextRef: React.RefObject<(contextId: string) => void>;
  readonly skillsRef: React.RefObject<ReadonlyArray<ServerProviderSkill>>;
  readonly latestValueRef: React.RefObject<string>;
} {
  const onChangeRef = useRef(input.onChange);
  const onCommandKeyDownRef = useRef(input.onCommandKeyDown);
  const onRemoveTerminalContextRef = useRef(input.onRemoveTerminalContext);
  const skillsRef = useRef(input.skills);
  const latestValueRef = useRef(input.value);
  useEffect(() => {
    onChangeRef.current = input.onChange;
    onCommandKeyDownRef.current = input.onCommandKeyDown;
    onRemoveTerminalContextRef.current = input.onRemoveTerminalContext;
  }, [input.onChange, input.onCommandKeyDown, input.onRemoveTerminalContext]);
  useEffect(() => {
    skillsRef.current = input.skills;
  }, [input.skills]);
  useLayoutEffect(() => {
    latestValueRef.current = input.value;
  }, [input.value]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [controller] = useState(() =>
    createComposerPromptEditorController({
      initialValue: input.initialValue,
      initialCursor: input.initialCursor,
      initialTerminalContextIds: input.initialTerminalContextIds,
      onChange: (...args: Parameters<NonNullable<ComposerPromptEditorOnChange>>) =>
        onChangeRef.current(...args),
      setIsEmpty,
      latestValue: () => latestValueRef.current,
    }),
  );
  return {
    controller,
    isEmpty,
    onChangeRef,
    onCommandKeyDownRef,
    onRemoveTerminalContextRef,
    skillsRef,
    latestValueRef,
  };
}

type ComposerPromptEditorOnChange = (
  nextValue: string,
  nextCursor: number,
  expandedCursor: number,
  cursorAdjacentToMention: boolean,
  terminalContextIds: string[],
) => void;
